import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import { CampaignMediaList } from '../dtos/campaignMedia';
import { accessToken, getAuthDetails } from '../utils/helper';
import Cookies from 'universal-cookie';

const cookies = new Cookies(null, { path: '/' });

interface CampaignMediaState {
  campaignMediaList:
    | { data: CampaignMediaList[]; total: number; totalPages: number }
    | { data: []; total: 0; totalPages: 0 };
  loading: boolean;
  error: string | null;
  addCampaignMediaResponse: { message: string } | null;
  addCampaignMediaError: string | null;
  updateCampaignMediaResponse: { message: string } | null;
  updateCampaignMediaError: string | null;
  deleteCampaignMediaResponse: { message: string } | null;
  deleteCampaignMediaError: string | null;
}

const initialState: CampaignMediaState = {
  campaignMediaList: { data: [], total: 0, totalPages: 0 },
  addCampaignMediaResponse: null,
  updateCampaignMediaResponse: null,
  deleteCampaignMediaResponse: null,
  loading: false,
  error: null,
  addCampaignMediaError: null,
  updateCampaignMediaError: null,
  deleteCampaignMediaError: null,
};

export const getAllCampaignMediasTrigger = createAsyncThunk(
  'get/campaign-media',
  async ({
    page,
    limit,
    search,
    clientId,
    sortOrder,
    sortField,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    clientId: number;
    sortOrder?: string;
    sortField?: string;
  }) => {
    try {
      const queryParams = new URLSearchParams();
      if (clientId) queryParams.append('clientId', clientId.toString());
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (sortField) queryParams.append('sortField', sortField);

      const response = await api.get(`/campaign-media?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [], totalPages: 0 };
    }
  },
);

export const addCampaignMediaTrigger = createAsyncThunk<
  { message: string },
  FormData
>('/campaign-media/add', async (request: FormData) => {
  const response = await api.post<{ message: string }>(
    `/campaign-media/add`,
    request,
    {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${getAuthDetails().accessToken}`,
        'Content-Type': 'multipart/form-data',
        'x-csrf-token': cookies.get('XSRF-TOKEN'),
      },
    },
  );
  return response.data;
});

export const updateCampaignMediaTrigger = createAsyncThunk<
  { message: string },
  { id: number; request: FormData }
>('/campaign-media/update/:id', async ({ id, request }) => {
  const response = await api.patch<{ message: string }>(
    `/campaign-media/update/${id}`,
    request,
    {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${getAuthDetails().accessToken}`,
        'Content-Type': 'multipart/form-data',
        'x-csrf-token': cookies.get('XSRF-TOKEN'),
      },
    },
  );
  return response.data;
});

export const deleteCampaignMediaTrigger = createAsyncThunk<
  { message: string },
  number | undefined
>('/campaign-media/delete/:id', async (id) => {
  const response = await api.delete<{ message: string }>(
    `/campaign-media/delete/${id}`,
    accessToken(),
  );
  return response.data;
});

export const campaignMediaBulkUploadTrigger = createAsyncThunk<
  { message: string },
  FormData
>('/campaign-media/bulk-upload', async (request: FormData) => {
  const response = await api.post<{ message: string }>(
    `/campaign-media/bulk-upload`,
    request,
    {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${getAuthDetails().accessToken}`,
        'Content-Type': 'multipart/form-data',
        'x-csrf-token': cookies.get('XSRF-TOKEN'),
      },
    },
  );
  return response.data;
});

export const getPendingAndFailedCampaignMediaFilesTrigger = createAsyncThunk(
  'campaign-media/getPendingAndFailedFiles',
  async () => {
    try {
      const response = await api.get(`/campaign-media/pending-failed/files`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [] };
    }
  },
);
export const getCompletedCampaignMediaFilesTrigger = createAsyncThunk(
  'campaign-media/getCompletedFiles',
  async () => {
    try {
      const response = await api.get(`/campaign-media/completed/files`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [] };
    }
  },
);

// Create slice
const campaignMediaSlice = createSlice({
  name: 'campaignMedia',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllCampaignMediasTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllCampaignMediasTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.campaignMediaList = action.payload;
      })
      .addCase(getAllCampaignMediasTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load posts';
      })
      .addCase(addCampaignMediaTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(addCampaignMediaTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.addCampaignMediaResponse = action.payload;
      })
      .addCase(addCampaignMediaTrigger.rejected, (state, action) => {
        state.loading = false;
        state.addCampaignMediaError = action.error.message || 'Failed to load posts';
      })
      .addCase(updateCampaignMediaTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCampaignMediaTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.updateCampaignMediaResponse = action.payload;
      })
      .addCase(updateCampaignMediaTrigger.rejected, (state, action) => {
        state.loading = false;
        state.updateCampaignMediaError = action.error.message || 'Failed to load posts';
      })
      .addCase(deleteCampaignMediaTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCampaignMediaTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteCampaignMediaResponse = action.payload;
      })
      .addCase(deleteCampaignMediaTrigger.rejected, (state, action) => {
        state.loading = false;
        state.deleteCampaignMediaError = action.error.message || 'Failed to load posts';
      });
  },
});

// Export the reducer to be added to the store
export default campaignMediaSlice.reducer;
